import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

export const Ph = ({ children }) => <span className="ph">{children}</span>;

export const renderPh = (text) => {
  const parts = String(text).split(/(\[\[[^\]]+\]\]|\[VERIFIED[^\]]+\])/g);
  return parts.map((p, i) =>
    p.startsWith("[[") || p.startsWith("[VERIFIED") ? <Ph key={i}>{p}</Ph> : <React.Fragment key={i}>{p}</React.Fragment>
  );
};

export const Reveal = ({ children, delay = 0, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    {...rest}
  >
    {children}
  </motion.div>
);

export const SectionLabel = ({ children }) => {
  if (!children) return null;
  return <div className="mono accent text-[10px] tracking-[0.3em] mb-6">{children}</div>;
};

export const Grain = () => (
  <svg className="grain" width="100%" height="100%">
    <filter id="grainF">
      <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" />
    </filter>
    <rect width="100%" height="100%" filter="url(#grainF)" />
  </svg>
);

export const Cursor = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const move = (e) => {
      el.style.left = `${e.clientX}px`;
      el.style.top = `${e.clientY}px`;
    };
    const over = (e) => {
      if (e.target.closest("button, a, input, select, label, [data-cta]")) el.classList.add("grow");
      else el.classList.remove("grow");
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseover", over);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
    };
  }, []);
  return <div ref={ref} className="cursor-dot" data-testid="custom-cursor" />;
};

export const Accordion = ({ items, testPrefix }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="border-t border-white/10">
      {items.map((it, i) => (
        <div key={i} className="border-b border-white/10">
          <button
            data-testid={`${testPrefix}-item-${i + 1}`}
            onClick={() => setOpen(open === i ? -1 : i)}
            className="w-full flex items-center gap-5 py-5 text-left group"
          >
            <span className="mono accent text-xs w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
            <span className="font-bold uppercase tracking-wide text-sm md:text-base flex-1 group-hover:text-[#4D9FFF] transition-colors duration-200">{it.q || it.t}</span>
            <span className={`accent text-xl transition-transform duration-300 ${open === i ? "rotate-45" : ""}`}>+</span>
          </button>
          <div className={`acc-body ${open === i ? "open" : ""}`}>
            <p className="pb-6 pl-[52px] pr-8 text-[15px] leading-relaxed text-white/55 max-w-2xl">{it.a || it.b}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const SectionBg = ({ img, opacity = 0.18 }) => (
  <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${img})`, opacity }} />
    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #0D2547 0%, rgba(13,37,71,0.45) 32%, rgba(13,37,71,0.45) 68%, #0D2547 100%)" }} />
  </div>
);
